
import React from "react";
import { TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Invoice } from "@/types/payments";
import { ArrowUpDown } from "lucide-react";
import { useTranslations } from "use-intl";

interface InvoiceTableHeaderProps {
  showCheckboxes: boolean;
  toggleSelectAll: () => void;
  handleSort: (key: keyof Invoice) => void;
  renderSortIcon: (key: keyof Invoice) => React.ReactNode;
  filteredInvoices: Invoice[];
  selectedInvoices: string[];
  sortConfig?: { key: keyof Invoice | null; direction: 'asc' | 'desc' | 'none' };
}

const InvoiceTableHeader: React.FC<InvoiceTableHeaderProps> = ({
  showCheckboxes,
  toggleSelectAll,
  handleSort,
  renderSortIcon,
  filteredInvoices,
  selectedInvoices,
  sortConfig
}) => {
  const t = useTranslations("consultantPayables.table");
  // Only render sort icons for the currently sorted column
  const shouldShowSortIcon = (key: keyof Invoice) => {
    return sortConfig?.key === key && sortConfig.direction !== 'none';
  };

  // Show the default ArrowUpDown icon for columns that aren't sorted
  const renderHeaderContent = (title: string, key: keyof Invoice) => {
    return (
      <div className="flex items-center">
        {title}
        {shouldShowSortIcon(key) ? (
          <span className="ml-1">{renderSortIcon(key)}</span>
        ) : (
          <span className="ml-1"><ArrowUpDown className="h-4 w-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" /></span>
        )}
      </div>
    );
  };

  return (
    <TableHeader>
      <TableRow>
        {showCheckboxes && (
          <TableHead className="w-12">
            <Checkbox 
              checked={selectedInvoices.length === filteredInvoices.length && filteredInvoices.length > 0} 
              onCheckedChange={toggleSelectAll} 
              aria-label={t("selectAll")} 
            />
          </TableHead>
        )}
        <TableHead className="cursor-pointer group" onClick={() => handleSort('status')}>
          {renderHeaderContent(t("columns.status"), 'status')}
        </TableHead>
        <TableHead className="cursor-pointer group" onClick={() => handleSort('vendor')}>
          {renderHeaderContent(t("columns.vendor"), 'vendor')}
        </TableHead>
        <TableHead className="cursor-pointer group" onClick={() => handleSort('id')}>
          {renderHeaderContent(t("columns.reference"), 'id')}
        </TableHead>
        <TableHead className="cursor-pointer group" onClick={() => handleSort('sourceAccountNumber')}>
          {renderHeaderContent(t("columns.sourceAccount"), 'sourceAccountNumber')}
        </TableHead>
        <TableHead className="cursor-pointer group" onClick={() => handleSort('amount')}>
          {renderHeaderContent(t("columns.amount"), 'amount')}
        </TableHead>
        <TableHead className="cursor-pointer group" onClick={() => handleSort('dueDate')}>
          {renderHeaderContent(t("columns.dueDate"), 'dueDate')}
        </TableHead>
        <TableHead className="cursor-pointer group" onClick={() => handleSort('paymentDate')}>
          {renderHeaderContent(t("columns.paymentDate"), 'paymentDate')}
        </TableHead>
        <TableHead className="w-10">{t("columns.actions")}</TableHead>
      </TableRow>
    </TableHeader>
  );
};

export default InvoiceTableHeader;
